import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AttrezzaturePort } from './attrezzatture.port';
import {
  Attrezzatura,
  CreateAttrezzaturaDto,
  UpdateAttrezzaturaDto,
  AttrezzatureQueryParams,
  PaginatedResponse,
  AttrezzaturaAssociazione,
  AttrezzaturaAssociazioniQueryParams,
  SetAvailabilityDto,
} from '../../models/attrezzature.model';
import { environment } from '../../../environments/environment';
import {TenantService} from '../tenant.service';

@Injectable({ providedIn: 'root' })
export class AttrezzatureHttpService implements AttrezzaturePort {
  private http = inject(HttpClient);
  private tenantService = inject(TenantService);
  private base = `${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/attrezzature`;
  getList(queryParams?: AttrezzatureQueryParams): Observable<PaginatedResponse<Attrezzatura>> {
    let params = new HttpParams();

    if (queryParams) {
      if (queryParams.size != null) params = params.set('size', queryParams.size.toString());
      if (queryParams.page != null) params = params.set('page', queryParams.page.toString());
      if (queryParams.wordSearch) params = params.set('wordSearch', queryParams.wordSearch);
      if (queryParams.idCantiere != null) params = params.set('idCantiere', queryParams.idCantiere.toString());
      if (queryParams.stato) params = params.set('stato', queryParams.stato);
      if (queryParams.disponibilita) params = params.set('disponibilita', queryParams.disponibilita);
      if (queryParams.archiviato != null) params = params.set('archiviato', String(queryParams.archiviato));
      if (queryParams.givenToCantiere != null) {
        params = params.set('givenToCantiere', String(queryParams.givenToCantiere));
      }
      if (Array.isArray(queryParams.sort) && queryParams.sort.length) {
        queryParams.sort.forEach(s => {
          const v = (s ?? '').trim();
          if (!v) return;
          params = params.append('sort', v);
        });
      }
    }

    return this.http.get<PaginatedResponse<Attrezzatura>>(this.base, { params });
  }

  getById(id: number): Observable<Attrezzatura> {
    return this.http.get<Attrezzatura>(`${this.base}/${id}`);
  }

  create(dto: CreateAttrezzaturaDto): Observable<number> {
    return this.http.post<number>(this.base, dto);
  }

  update(id: number, dto: UpdateAttrezzaturaDto): Observable<number> {
    return this.http.patch<number>(`${this.base}/${id}`, dto);
  }

  setAvailable(id: number): Observable<number> {
    return this.http.patch<number>(`${this.base}/${id}/setAvailable`, {});
  }

  setAvailability(id: number, dto: SetAvailabilityDto): Observable<number> {
    let params = new HttpParams().set('disponibilita', dto.disponibilita);
    if (dto.motivazione) params = params.set('motivazione', dto.motivazione);
    if (dto.dataRitorno) params = params.set('dataRitorno', dto.dataRitorno);
    return this.http.patch<number>(`${this.base}/${id}/setAvailability`, {}, { params });
  }

  associateToCantiere(id: number, idCantiere: number): Observable<number> {
    return this.http.patch<number>(`${this.base}/${id}/associateToCantiere/${idCantiere}`, {});
  }

  endCantiereAssociation(id: number): Observable<number> {
    return this.http.patch<number>(`${this.base}/${id}/endCantiereAssociation`, {});
  }

  associateToFornitore(id: number, idFornitore: number, dataUscita: string): Observable<number> {
    const params = new HttpParams().set('dataUscita', dataUscita);
    return this.http.patch<number>(`${this.base}/${id}/associateToFornitore/${idFornitore}`, {}, { params });
  }

  endFornitoreAssociation(id: number, dataRitorno: string): Observable<number> {
    const params = new HttpParams().set('dataRitorno', dataRitorno);
    return this.http.patch<number>(`${this.base}/${id}/endFornitoreAssociation`, {}, { params });
  }

  getStoricoAssociazioni(queryParams?: AttrezzaturaAssociazioniQueryParams): Observable<PaginatedResponse<AttrezzaturaAssociazione>> {
    let params = new HttpParams();
    if (queryParams?.idAttrezzatura != null) params = params.set('idAttrezzatura', queryParams.idAttrezzatura.toString());
    if (queryParams?.idFornitore != null) params = params.set('idFornitore', queryParams.idFornitore.toString());
    if (queryParams?.page != null) params = params.set('page', queryParams.page.toString());
    if (queryParams?.size != null) params = params.set('size', queryParams.size.toString());
    return this.http.get<PaginatedResponse<AttrezzaturaAssociazione>>(`${this.base}/storicoAssociazioni`, { params });
  }

  archive(id: number, archivia: boolean, dataRitorno?: string): Observable<number> {
    let params = new HttpParams().set('archivia', String(archivia));
    if (dataRitorno) params = params.set('dataRitorno', dataRitorno);
    return this.http.patch<number>(`${this.base}/${id}/archive`, {}, { params });
  }
}
